// «Не узнаны»: встречи календаря, по которым нельзя однозначно понять, чей это клиент (приложение не угадывает),
// встречи, название которых не сходится с клиентом их сессии, и клиенты, заведённые синхронизацией сами. Один раз
// сказать — дальше такие встречи узнаются сами.
import { RU_DAYS, dowOf, plural, ruDateShort } from '@praktika/core';
import { afterChange, call, rpc } from '../lib/api.ts';
import { $, $$, esc, toast } from '../lib/dom.ts';
import { SCREENS, render, title } from '../lib/nav.ts';
import { closeSheet, openSheet, opt, promptSheet } from '../lib/sheet.ts';
import { pickClient } from './common.ts';

interface Who { id: string; name: string; archived?: boolean }
interface Item {
  eventId: string; summary: string; date: string; start: string; dur: number; recurring: string | null;
  reason: 'none' | 'many' | 'archived' | 'mismatch'; candidates: Who[]; similar: Who[]; sessionId: string | null;
  guess: string; generic: boolean;
}
interface CheckClient { id: string; name: string; sessions: number }

let data: { items: Item[]; clients: CheckClient[] } | null = null, loading = false;

// после действия — перечитать и список, и состояние кабинета (счётчики на «Сегодня»)
function done(r: { message?: string }): Promise<void> {
  data = null;
  return afterChange(r.message || 'Готово');
}

function why(x: Item): string {
  const names = (l: Who[]) => l.map((c) => c.name).join(', ');
  if (x.reason === 'mismatch') return 'записана к ' + (x.candidates[0]?.name ?? '?') + ', а по названию — ' +
    (x.candidates[1]?.name ?? '?');
  if (x.reason === 'many') return 'подходят несколько: ' + names(x.candidates);
  if (x.reason === 'archived') return 'клиент в архиве: ' + names(x.candidates);
  return x.similar.length ? 'похоже на: ' + names(x.similar) : 'клиент не найден';
}

function resolve(x: Item, p: Record<string, unknown>): void {
  closeSheet();
  void call<{ message?: string }>('resolveEvent', x.eventId, p).then(done, () => {});
}

function notSessionSheet(x: Item): void {
  const sh = openSheet('<div class="ttl">Не сессия</div><p class="sub">«' + esc(x.summary) + '» — личное дело, ' +
    'в сессии не превращается.</p>' + opt('', '', 'Только эта встреча') +
    (x.generic ? '' : opt('', '', 'И все такие', 'встречи «' + x.summary + '» больше не показывать')) +
    (x.recurring ? opt('', '', 'Вся серия', 'повторяющаяся встреча целиком') : ''));
  const acts = [() => resolve(x, { action: 'notSession' })];
  if (!x.generic) acts.push(() => resolve(x, { action: 'notSession', always: true }));
  if (x.recurring) acts.push(() => resolve(x, { action: 'notSession', series: true }));
  $$('button.opt', sh).forEach((b, i) => { b.onclick = () => acts[i]!(); });
}

function itemSheet(x: Item): void {
  const when = RU_DAYS[dowOf(x.date)]!.toLowerCase() + ' ' + ruDateShort(x.date) + ' · ' + x.start;
  let html = '<div class="ttl">' + esc(x.summary || 'без названия') + '</div><p class="sub">' + esc(when) + ' · ' +
    esc(why(x)) + '</p>';
  const acts: Array<() => void> = [];
  if (x.reason === 'mismatch') {
    const [was, now] = x.candidates;
    if (now) { html += opt('ok', '', 'Перенести к «' + now.name + '»', 'цена и начисление — по его карточке'); acts.push(() => resolve(x, { action: 'move', clientId: now.id })); }
    if (was) { html += opt('', '', 'Оставить у «' + was.name + '»', 'больше не спрашивать, пока название то же'); acts.push(() => resolve(x, { action: 'keep' })); }
    html += opt('', '', 'Перенести к другому клиенту…');
    acts.push(() => { closeSheet(); pickClient((id) => resolve(x, { action: 'move', clientId: id })); });
  } else {
    const people = x.reason === 'none' ? x.similar : x.candidates;
    for (const c of people) {
      html += opt('ok', '', (c.archived ? 'Вернуть из архива: ' : 'Это ') + c.name, c.archived ? 'и записать сессию' :
        x.reason === 'many' ? 'только эта встреча' : 'и запомнить такое написание');
      acts.push(() => resolve(x, { action: 'client', clientId: c.id, remember: x.reason === 'none' }));
    }
    html += opt('', '', 'Другой клиент…', 'выбрать из списка');
    acts.push(() => { closeSheet(); pickClient((id) => resolve(x, { action: 'client', clientId: id, remember: true })); });
    html += opt('', '', 'Новый клиент' + (x.guess ? ' «' + x.guess + '»' : ''), 'завести и записать сессию');
    acts.push(() => { closeSheet(); promptSheet('Имя нового клиента', x.guess, 'text', (v) => {
      if (String(v).trim()) void call<{ message?: string }>('resolveEvent', x.eventId, { action: 'new', name: v }).then(done, () => {});
    }); });
    html += '<div class="gap"></div>' + opt('no', '', 'Не сессия', 'личное дело');
    acts.push(() => { closeSheet(); notSessionSheet(x); });
  }
  const sh = openSheet(html);
  $$('button.opt', sh).forEach((b, i) => { b.onclick = () => acts[i]!(); });
}

function clientSheet(c: CheckClient): void {
  const sh = openSheet('<div class="ttl">' + esc(c.name) + '</div><p class="sub">Клиент заведён синхронизацией сам ' +
    '(по прежнему правилу) · сессий ' + c.sessions + '. Проверьте, настоящий ли это клиент.</p>' +
    opt('ok', '', 'Всё верно — это клиент') + opt('', '', 'Это дубль — объединить с…', 'сессии и приметы перейдут к нему') +
    opt('no', '', 'Не клиент — удалить', c.sessions ? 'вместе с сессиями (' + c.sessions + '), такие встречи — личные дела' :
      'такие встречи — личные дела'));
  const acts = [
    () => { closeSheet(); void call<{ message?: string }>('reviewClient', c.id, 'ok').then(done, () => {}); },
    () => { closeSheet(); pickClient((id) => {
      if (id === c.id) { toast('Это тот же клиент', true); return; }
      void call<{ message?: string }>('mergeClients', c.id, id).then(done, () => {});
    }); },
    () => {
      if (!confirm('Удалить «' + c.name + '»' + (c.sessions ? ' и его сессии (' + c.sessions + ')' : '') + '?')) return;
      closeSheet();
      void call<{ message?: string }>('reviewClient', c.id, 'delete').then(done, () => {});
    },
  ];
  $$('button.opt', sh).forEach((b, i) => { b.onclick = () => acts[i]!(); });
}

SCREENS.unknown = (app, p) => {
  if (!p.opened) { p.opened = true; data = null; }       // каждый заход на экран — свежий список
  if (!data) {
    app.innerHTML = title('Не узнаны') + '<div class="empty">Загружаю…</div>';
    if (!loading) {
      loading = true;
      rpc<{ items: Item[]; clients: CheckClient[] }>('unknownList').then((r) => { data = r; loading = false; render(); },
        (e: Error) => { loading = false; toast(e.message, true); });
    }
    return;
  }
  const { items, clients } = data;
  let html = title('Не узнаны', items.length ? items.length + ' ' + plural(items.length, 'встреча', 'встречи', 'встреч') : '') +
    '<div class="p-sum">Приложение не угадывает: если по встрече нельзя точно понять, чей это клиент, она ждёт здесь, ' +
    'а её время в расписании занято. Один раз скажите — дальше такие встречи узнаются сами.</div>';
  if (items.length) {
    let day = '';
    html += '<div class="p-list">';
    for (const x of items) {
      if (x.date !== day) {
        day = x.date;
        html += '<div class="list-h">' + esc(RU_DAYS[dowOf(x.date)] + ' ' + ruDateShort(x.date)) + '</div>';
      }
      html += '<button class="p-row" data-u="' + esc(x.eventId) + '"><div class="tm">' + esc(x.start) + '</div>' +
        '<div class="who"><div class="n">' + esc(x.summary || 'без названия') + '</div><div class="i">' + esc(why(x)) +
        '</div></div><div class="st' + (x.reason === 'mismatch' ? ' off' : '') + '">' +
        (x.reason === 'mismatch' ? 'не сходится' : 'разобрать') + '</div></button>';
    }
    html += '</div>';
  } else html += '<div class="empty">Все встречи узнаны</div>';
  if (clients.length) {
    html += '<div class="list-h">Заведены сами — проверить</div><div class="p-list">' + clients.map((c) =>
      '<button class="p-row" data-c="' + esc(c.id) + '"><div class="who"><div class="n">' + esc(c.name) +
      '</div><div class="i">сессий ' + c.sessions + '</div></div><div class="st">проверить</div></button>').join('') + '</div>';
  }
  app.innerHTML = html;
  $$('[data-u]', app).forEach((b) => {
    b.onclick = () => { const x = items.find((i) => i.eventId === b.getAttribute('data-u')); if (x) itemSheet(x); };
  });
  $$('[data-c]', app).forEach((b) => {
    b.onclick = () => { const c = clients.find((i) => i.id === b.getAttribute('data-c')); if (c) clientSheet(c); };
  });
};
